import React, {
	FC,
	PropsWithChildren,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';

import {
	FrequencyDto,
	IScheduledJobConfiguration
} from '../../../interfaces/ScheduledJobConfiguration';

import { SchedulingSectionContext } from './schedulingSectionContext';
import { SchedulingSectionContextTypeProps } from './SchedulingSectionContext.types';

export const SchedulingSectionContextWrapper: FC<
	PropsWithChildren<SchedulingSectionContextTypeProps>
> = (props) => {
	const { children, initialConfig, onConfigChange, error } = props;

	const [config, setConfig] = useState<IScheduledJobConfiguration>(initialConfig);
	const previousFrequencyRef = useRef<FrequencyDto | undefined>(initialConfig.frequency);

	useEffect(() => {
		setConfig(initialConfig);
		previousFrequencyRef.current = initialConfig.frequency;
	}, [initialConfig]);

	useEffect(() => {
		const currentFrequency = config.frequency;
		const previousFrequency = previousFrequencyRef.current;

		if (currentFrequency !== previousFrequency && previousFrequency !== undefined) {
			setConfig((prevConfig) => {
				const cleanedConfig = { ...prevConfig };

				if (currentFrequency === FrequencyDto.MONTHLY) {
					cleanedConfig.wdays = [];
				}

				if (
					currentFrequency === FrequencyDto.WEEKLY ||
					currentFrequency === FrequencyDto.EVERY_OTHER_WEEK
				) {
					cleanedConfig.mdays = [];
					cleanedConfig.lastDayOfMonth = false;
					cleanedConfig.startMonth = undefined;
					cleanedConfig.startYear = undefined;
					cleanedConfig.months = [];
				}

				return cleanedConfig;
			});
		}

		previousFrequencyRef.current = currentFrequency;
	}, [config?.frequency]);

	const updateConfig = useCallback((updates: Partial<IScheduledJobConfiguration>) => {
		setConfig((prevConfig) => {
			const newConfig = { ...prevConfig, ...updates };
			return newConfig;
		});
	}, []);

	// Notify parent whenever config changes
	useEffect(() => {
		onConfigChange(config);
	}, [config, onConfigChange]);

	const value = useMemo(
		() => ({
			config,
			updateConfig,
			error
		}),
		[config, updateConfig, error]
	);

	return (
		<SchedulingSectionContext.Provider value={value}>{children}</SchedulingSectionContext.Provider>
	);
};
